/**
 * Path Simplification Utilities
 *
 * Implements the Douglas-Peucker algorithm for path simplification
 * to create multiple detail levels of trail waypoints for progressive loading.
 */
import { TrailWaypoint } from '@/types/hikingTrail';

// Extended type for waypoints that might have elevation data
interface ExtendedWaypoint extends TrailWaypoint {
  elevation?: number;
}

export interface BoundingBox {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface DetailLevels {
  summary: TrailWaypoint[];
  low: TrailWaypoint[];
  medium: TrailWaypoint[];
  high: TrailWaypoint[];
}

/**
 * Calculate perpendicular distance from point to line
 * @param point The point to calculate distance for
 * @param lineStart Start point of the line
 * @param lineEnd End point of the line
 * @returns The perpendicular distance
 */
function perpendicularDistance(
  point: TrailWaypoint,
  lineStart: TrailWaypoint,
  lineEnd: TrailWaypoint
): number {
  const { latitude: x, longitude: y } = point.coordinates;
  const { latitude: x1, longitude: y1 } = lineStart.coordinates;
  const { latitude: x2, longitude: y2 } = lineEnd.coordinates;

  // Line equation: ax + by + c = 0
  const a = y1 - y2;
  const b = x2 - x1;
  const c = x1 * y2 - x2 * y1;

  // Distance formula: |ax + by + c| / sqrt(a² + b²)
  return Math.abs(a * x + b * y + c) / Math.sqrt(a * a + b * b);
}

/**
 * Douglas-Peucker algorithm for path simplification
 * @param waypoints Array of waypoint objects
 * @param tolerance Maximum allowed distance from original path
 * @returns Simplified array of waypoints
 */
export function simplifyPath(
  waypoints: TrailWaypoint[],
  tolerance: number
): TrailWaypoint[] {
  if (waypoints.length <= 2) return waypoints;

  // Find the point with the maximum distance
  let maxDistance = 0;
  let index = 0;

  const firstPoint = waypoints[0];
  const lastPoint = waypoints[waypoints.length - 1];

  for (let i = 1; i < waypoints.length - 1; i++) {
    const distance = perpendicularDistance(waypoints[i], firstPoint, lastPoint);

    if (distance > maxDistance) {
      maxDistance = distance;
      index = i;
    }
  }

  // If max distance is greater than tolerance, recursively simplify
  if (maxDistance > tolerance) {
    // Recursive simplification of the two segments
    const firstSegment = simplifyPath(waypoints.slice(0, index + 1), tolerance);

    const secondSegment = simplifyPath(waypoints.slice(index), tolerance);

    // Concatenate the two simplified segments
    return [...firstSegment.slice(0, -1), ...secondSegment];
  }

  // Base case - just return first and last points
  return [firstPoint, lastPoint];
}

/**
 * Identify critical points in a trail that should always be included in summaries
 * Critical points include significant turns, elevation changes, and POI locations
 * @param waypoints Full array of waypoints
 * @returns Array of critical waypoints
 */
export function identifyCriticalPoints(
  waypoints: TrailWaypoint[]
): TrailWaypoint[] {
  if (waypoints.length <= 2) return [];

  const criticalPoints: TrailWaypoint[] = [];
  const waypointCount = waypoints.length;

  // Add waypoints with names/descriptions as they are likely important landmarks
  waypoints.forEach((waypoint, index) => {
    // Skip first and last points (they're always included)
    if (index === 0 || index === waypointCount - 1) return;

    if (waypoint.name || waypoint.description || waypoint.photoUrl) {
      criticalPoints.push(waypoint);
    }
  });

  // Check for elevation data if available (through extended waypoint properties)
  // This is for future proofing when elevation data might be added
  let lastElevation: number | undefined;
  waypoints.forEach((waypoint, index) => {
    // Skip first and last points
    if (index === 0 || index === waypointCount - 1) {
      const extendedWp = waypoint as ExtendedWaypoint;
      if (extendedWp.elevation !== undefined) {
        lastElevation = extendedWp.elevation;
      }
      return;
    }

    const extendedWp = waypoint as ExtendedWaypoint;
    if (extendedWp.elevation !== undefined && lastElevation !== undefined) {
      const elevationChange = Math.abs(extendedWp.elevation - lastElevation);
      // Significant elevation change (more than 50m)
      if (elevationChange > 50) {
        criticalPoints.push(waypoint);
      }
      lastElevation = extendedWp.elevation;
    }
  });

  // Add waypoints with significant direction changes
  if (waypointCount > 4) {
    for (let i = 1; i < waypointCount - 1; i++) {
      const prev = waypoints[i - 1];
      const current = waypoints[i];
      const next = waypoints[i + 1];

      // Calculate bearing changes to detect significant turns
      const bearingChange = calculateBearingChange(
        prev.coordinates,
        current.coordinates,
        next.coordinates
      );

      // If bearing change is more than 30 degrees, consider it significant
      if (Math.abs(bearingChange) > 30) {
        criticalPoints.push(current);
      }
    }
  }

  // Deduplicate points
  const uniqueIds = new Set<string>();
  return criticalPoints.filter((point) => {
    if (uniqueIds.has(point.id)) return false;
    uniqueIds.add(point.id);
    return true;
  });
}

/**
 * Calculate bearing change at a waypoint to detect significant turns
 */
function calculateBearingChange(
  coord1: { latitude: number; longitude: number },
  coord2: { latitude: number; longitude: number },
  coord3: { latitude: number; longitude: number }
): number {
  const bearing1 = calculateBearing(coord1, coord2);
  const bearing2 = calculateBearing(coord2, coord3);

  let change = bearing2 - bearing1;

  // Normalize to -180 to 180
  if (change > 180) change -= 360;
  if (change < -180) change += 360;

  return change;
}

/**
 * Calculate bearing between two coordinates
 */
function calculateBearing(
  start: { latitude: number; longitude: number },
  end: { latitude: number; longitude: number }
): number {
  const startLat = toRadians(start.latitude);
  const startLng = toRadians(start.longitude);
  const endLat = toRadians(end.latitude);
  const endLng = toRadians(end.longitude);

  const y = Math.sin(endLng - startLng) * Math.cos(endLat);
  const x =
    Math.cos(startLat) * Math.sin(endLat) -
    Math.sin(startLat) * Math.cos(endLat) * Math.cos(endLng - startLng);

  let bearing = Math.atan2(y, x);
  bearing = toDegrees(bearing);
  return (bearing + 360) % 360;
}

/**
 * Convert degrees to radians
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Convert radians to degrees
 */
function toDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}

/**
 * Create multiple levels of trail detail
 * @param waypoints Original waypoints array
 * @returns Object with different detail levels
 */
export function createDetailLevels(waypoints: TrailWaypoint[]): DetailLevels {
  if (!waypoints || waypoints.length === 0) {
    return {
      summary: [],
      low: [],
      medium: [],
      high: [],
    };
  }

  // Always include start and end points in summary
  let summary = [waypoints[0], waypoints[waypoints.length - 1]];

  // Add critical points to summary
  const criticalPoints = identifyCriticalPoints(waypoints);
  summary = [...summary, ...criticalPoints];

  // Sort by position and deduplicate
  const uniqueIds = new Set<string>();
  summary = summary
    .filter((wp) => {
      if (uniqueIds.has(wp.id)) return false;
      uniqueIds.add(wp.id);
      return true;
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // Generate different detail levels
  return {
    // ~5-10 points for overview
    summary: summary.length < 5 ? simplifyPath(waypoints, 0.001) : summary,
    // ~15-20% of original points
    low: simplifyPath(waypoints, 0.0005),
    // ~40-50% of original points
    medium: simplifyPath(waypoints, 0.0002),
    // Original waypoints
    high: waypoints,
  };
}

/**
 * Calculate bounding box for array of waypoints
 */
export function calculateBoundingBox(waypoints: TrailWaypoint[]): BoundingBox {
  if (!waypoints || waypoints.length === 0) {
    return {
      north: 0,
      south: 0,
      east: 0,
      west: 0,
    };
  }

  let north = -90,
    south = 90,
    east = -180,
    west = 180;

  waypoints.forEach((wp) => {
    const { latitude, longitude } = wp.coordinates;
    north = Math.max(north, latitude);
    south = Math.min(south, latitude);
    east = Math.max(east, longitude);
    west = Math.min(west, longitude);
  });

  return { north, south, east, west };
}

/**
 * Split array into chunks of specified size
 */
export function chunkArray<T>(array: T[], size: number): T[][] {
  if (!array || array.length === 0) return [];

  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

/**
 * Check if two bounding boxes intersect
 */
export function boundingBoxesIntersect(
  box1: BoundingBox,
  box2: BoundingBox
): boolean {
  return (
    box1.west <= box2.east &&
    box1.east >= box2.west &&
    box1.south <= box2.north &&
    box1.north >= box2.south
  );
}
